import { FormBuilder, FormGroup } from '@angular/forms';
import { OnInit, Component, Input } from '@angular/core';
import { WizardStep } from '../../../wizard/wizard.component';
import { ReservationCalendar } from '../../../model/reservation';
import { BehaviorSubject } from 'rxjs';
import { ReservationSlot } from '../../../model/reservation-slot';
import { NotificationToastService } from '../../../services/notification-toast.service';
import { ReservationWizardService } from '../../../services/wizard/reservation/reservation.wizard.service';
import { ConfirmDialogService } from '../../../services/confirm-dialog.service';
import { AuthorizationService } from '../../../services/authorization.service';
import { MatDialog } from '@angular/material';
import { AuthorizationDialogComponent } from '../../dialogs/authorization/authorization-dialog.component';
import { AuthService } from '../../../services/auth.service';
import { TransferTimeService } from '../../../services/transfer-time.service';
import { PatientService } from 'src/app/services/patient.service';
import { PatientReservationsDialogComponent } from '../../dialogs/patient-reservations-dialog/patient-reservations-dialog.component';
import { ReservationService } from '../../../services/reservation.service';
import { EquipmentFilter } from '../../../model/equipment-filter';


@Component({
  selector: 'app-reservation-calendar-component',
  templateUrl: './reservation-calendar.component.html',
  styleUrls: ['./reservation-calendar.component.css'],
  providers: [{ provide: TransferTimeService, useClass: TransferTimeService }]
})
export class ReservationCalendarComponent implements OnInit, WizardStep {

  es: any;
  isInit: boolean;
  loading: boolean;
  loadingCalendar: boolean;
  reservationCalendarFormGroup: FormGroup;
  reservationCalendar: ReservationCalendar;
  slotsColumns = ['From', 'To', 'Doctor', 'PracticeRegionName', 'EquipmentName', 'Patient'];
  slotsDataSource = new BehaviorSubject<ReservationSlot[]>([]);
  doctors: string[] = [];
  centers: string[] = [];
  sessions: number[] = [];
  selectedCenters: string[] = [];
  equipments: string[] = [];
  equipmentsAvailability: EquipmentFilter[] = [];
  equipmentsAvailabilitySearching: boolean;
  seeDraft: boolean;
  seeNoRules: boolean;
  seeNoRulesRestriction: boolean;
  searchPatientReservations: boolean;
  creatingPendingReservation: boolean;
  canCreatePendingReservation: boolean;
  canChangeSessions: boolean;
  @Input('ocultarEquipment') ocultarEquipment: boolean;
  @Input('ocultarVerBorrador') ocultarVerBorrador: boolean;
  @Input('showReservationEquipmentAvailability') showReservationEquipmentAvailability: boolean;

  constructor(private notificationToastService: NotificationToastService
    , private formBuilder: FormBuilder
    , private confirmDialogService: ConfirmDialogService
    , private dialog: MatDialog
    , private authService: AuthService
    , private patientService: PatientService
    , private authorizationService: AuthorizationService
    , private transferTimeService: TransferTimeService
    , private reservationWizardService: ReservationWizardService) {
    this.seeNoRulesRestriction = this.authService.canSeeNoRulesRestriction();
    this.seeNoRules = this.authService.canSeeNoRules();
    this.seeDraft = this.authService.seeDraftReservationCalendar();
    this.canCreatePendingReservation = this.authService.canCreatePendingReservation();
    this.canChangeSessions = this.authService.canChangeReservationSessions();
    this.sessions = Array.from({length: 100}, (_, i) => i + 1);
    this.reservationCalendarFormGroup = this.formBuilder.group({
      Calendar: [''],
      Doctor: [''],
      Equipment: [''],
      Session: [1],
      EquipmentAvailability: [''],
      Center: [this.formBuilder.array([])],
      SeeBusy: [false],
      ManualSelection: [false],
      SeeDraft: [false],
      SeeNoRules: [false],
      SeeNoRulesRestriction: [false],
    });

    this.reservationCalendarFormGroup.controls['Session']
    .valueChanges.subscribe(value => {
      this.reservationWizardService.setSessions(value)
    });

  }

  private async filterDateSlots(date: any, physicianFullName: string,
    equipmentName: string, centerName: string[], seeBusy: boolean) {

    if (!date) {
      this.slotsDataSource.next([]);
      return;
    }

    let dateSlots: ReservationSlot[] = [];
    const physicianFullNameFilter = physicianFullName ? physicianFullName : null;
    const equipmentNameFilter = equipmentName ? equipmentName : null;
    const centerNameFilter = centerName ? centerName : null;

    if (this.reservationCalendar && this.reservationCalendar.ReservationSlots) {

      dateSlots = this.reservationCalendar.ReservationSlots
        .filter(d => new Date(d.Date).getDate() === date.getDate()
        && new Date(d.Date).getMonth() === date.getMonth()
        && new Date(d.Date).getFullYear() === date.getFullYear()
        && (!seeBusy ? d.MedicalAppointmentId == null : seeBusy)
        && (equipmentNameFilter == null || d.EquipmentName === equipmentNameFilter)
        && (centerNameFilter == null || centerNameFilter.length === 0 || centerNameFilter.indexOf(d.CenterName) > -1)
        && (physicianFullNameFilter == null || d.PhysicianFullName === physicianFullNameFilter));

      if ( physicianFullNameFilter == null &&
          this.authService.filterPhysicianInReservation() ) {
          dateSlots = dateSlots.filter
            (d => d.PhysicianId === this.authService.getPhysicianId());
      }

    }

    if (dateSlots && this.reservationCalendarFormGroup.value.SeeBusy) {
      const occupiedDateSlots = dateSlots.filter((d) => d.MedicalAppointmentId != null && d.PatientId == null);
      if (occupiedDateSlots.length > 0) {
        await this.reservationWizardService.completePatientInfo(occupiedDateSlots);
      }
    }

    this.slotsDataSource.next(dateSlots);
  }

  get completed(): boolean {
    return !this.reservationWizardService.existsReservationServiceWithOutSlot()
      && this.reservationWizardService.isValidReservationSlots()
      && !this.reservationWizardService.authorizationRequired();
  }

  validationSlotMessage(): string {
    return this.reservationWizardService.validationSlotMessage();
  }

  async seeDraftChange() {
    await this.reloadCalendar();
  }

  async seeNoRulesChange() {
    await this.reloadCalendar();
  }

  get valid(): boolean {
    return this.completed;
  }


  get selectedSlot(): number { return this.reservationWizardService.selectedSlotCount(); }

  get orderedPhysician(): string[] {
    if (this.doctors) {
      return this.doctors.sort();
    } else {
      return [];
    }
  }

  async activate() {
    try {

      if (!this.completed) {
        this.loading = true;

        this.reservationCalendar = await this.reservationWizardService
        .reservationCalendar();
        this.doctors = this.reservationCalendar.Doctors;
        this.equipments = this.reservationCalendar.Equipments;
        this.centers = this.reservationCalendar.Centers;
        this.reservationWizardService.clearAllReservationSlot();

        // cargo lo que ya viene configurado desde el server
        if (this.reservationCalendar.FirstFreeDate != null &&
            this.reservationCalendar.ReservationSlots != null) {

          this.reservationCalendarFormGroup.get('Calendar')
            .setValue(new Date(this.reservationCalendar.FirstFreeDate));

            this.reservationCalendar.ReservationSlots
            .filter((reservationSlot: ReservationSlot) => {
              if (reservationSlot.IsSelected) {
                this.reservationWizardService.setReservationSlot(reservationSlot);
                this.setReservationEquipmentAvailabilityIfRequired();
              }

          });

        }
      }
    } catch (err) {
      this.notificationToastService.error(JSON.stringify(err.error), 'Reserva');
    } finally {
      this.loading = false;
      this.isInit = true;
    }
  }

  async showPatientReservationSearchItems() {
    try {
      if ( this.reservationWizardService.getPatient() ) {
        this.searchPatientReservations = true;
        const reservations = await this.patientService
          .getReservationSearchItems(this.reservationWizardService.getPatient().Id);
        this.dialog.open(PatientReservationsDialogComponent, {
          width: '50%',
          data: reservations
        });
      }
    } catch (err) {
      this.notificationToastService.error(JSON.stringify(err.error), 'Reserva');
    } finally {
      this.searchPatientReservations = false;
    }
  }

  getDateColor(date: any): string {
    if (this.reservationCalendar && this.reservationCalendar.FreeDates
      && (this.reservationCalendar.FreeDates.findIndex(
        d => new Date(d).getDate() === date.day
        && new Date(d).getFullYear() === date.year
        && new Date(d).getMonth() === date.month) > -1)) {
      return '#7cc67c';
    } else {
      return 'inherit';
    }
  }

  slotSelected(slot: ReservationSlot): void {

    let auxSlot = slot;
    const practiceRegionIds: number[] = [];

    if (auxSlot.IsInconsistent)
      this.notificationToastService.info('No se puede dar un sobreturno sobre un turno inconsistente', 'Sobreturno');

    if (auxSlot.IsSelectable && !auxSlot.IsInconsistent) {

      if (!auxSlot.MedicalAppointmentId ||  this.reservationCalendarFormGroup.value.ManualSelection) {
        practiceRegionIds.push(auxSlot.PracticeRegionId);
        this.setSelectSlot(auxSlot);
      }

      if (!auxSlot.MedicalAppointmentId && !this.reservationCalendarFormGroup.value.ManualSelection) {

        while (auxSlot) {
          const slotDate = new Date(auxSlot.Date);
          const maxDate = new Date(slotDate.getFullYear(), slotDate.getMonth(), slotDate.getDate() + 1);

          auxSlot =  this.reservationCalendar.ReservationSlots.find(s =>
            practiceRegionIds.findIndex(p => p === s.PracticeRegionId) < 0
            && s.PracticeRegionId !== auxSlot.PracticeRegionId
            && !s.MedicalAppointmentId
            && new Date(s.Date) >= this.transferTimeService.getDateTo(auxSlot, s)
            && new Date(s.Date) < maxDate
            && s.IsSelectable);


          if (auxSlot) {
            practiceRegionIds.push(auxSlot.PracticeRegionId);
            this.setSelectSlot(auxSlot);
          }
        }
      }
    }
  }

  private setSelectSlot(slot: ReservationSlot) {

    const isCombo = this.reservationWizardService
      .getAllPrincipalPracticeRegion.length > 1;

    let noEsOcupadoOtraPracticaEnCombo = false;
    // busco todas las secciones seleccionadas de esa practica para deseleccionar
    this.reservationCalendar.ReservationSlots.filter(
      s => s.IsSelected === true && this.matchReservationSlot(s, slot, isCombo))
      .forEach((slotItem: ReservationSlot) => {
        slotItem.IsSelected = false;
        slotItem.Authorized = false;
        slotItem.BeforeOriginalMedicalAppointment = null;
        noEsOcupadoOtraPracticaEnCombo = true;
    });

    // if ( noEsOcupadoOtraPracticaEnCombo ) {

      slot.Authorized = false;
      slot.BeforeOriginalMedicalAppointment = null;
      slot.IsSelected = true;
      this.reservationWizardService.setReservationSlot(slot);

      // esto seria querer dar un sobre turno
      if (slot.MedicalAppointmentId > 0) {
        this.reservationSlotAuthorize(slot);
      }

      this.setReservationEquipmentAvailabilityIfRequired();
  }


  private async setReservationEquipmentAvailabilityIfRequired() {
    if (this.showReservationEquipmentAvailability) {
      this.equipmentsAvailabilitySearching = true;
      const info = await this.reservationWizardService.getReservationEquipmentAvailability();
      this.equipmentsAvailability = info.Equipments;
      if (info.Equipments.length == 0) {
        this.reservationCalendarFormGroup.patchValue({"EquipmentAvailability": null});
      } else {
        this.reservationCalendarFormGroup.patchValue({"EquipmentAvailability": info.Equipments[info.Index]});
        this.onEquipmentAvailabilitySelected(info.Equipments[info.Index]);
      }
      this.equipmentsAvailabilitySearching = false;
    }
  }

  onEquipmentAvailabilitySelected(equipment: EquipmentFilter) {
    this.reservationWizardService.forceReservationEquipment(equipment);
  }

  equipmentAvailabilityDisplayFn(equipment: EquipmentFilter) {
    return equipment.Name;
  }

  private matchReservationSlot(slot: ReservationSlot,
       selectedSlot: ReservationSlot, isCombo: boolean): boolean {
    if (isCombo) {
      return slot.PracticeRegionId === selectedSlot.PracticeRegionId;
    } else {
      return slot.ServiceId === selectedSlot.ServiceId;
    }
  }

  async onCenterSelectionChange(event) {
    if (event.isUserInput) {

      if (event.source.selected) {
        this.selectedCenters.push(event.source.value);
      } else {
        const index = this.selectedCenters.indexOf(event.source.value);
        this.selectedCenters.splice(index, 1);
      }

      await this.filterDateSlots(this.reservationCalendarFormGroup.value.Calendar
        , this.reservationCalendarFormGroup.value.Doctor
        , this.reservationCalendarFormGroup.value.Equipment
        , this.selectedCenters
        , this.reservationCalendarFormGroup.value.SeeBusy);
    }
  }

  async onEquipmentChange() {
    await this.filterDateSlots(this.reservationCalendarFormGroup.value.Calendar
      , this.reservationCalendarFormGroup.value.Doctor
      , null
      , this.selectedCenters
      , this.reservationCalendarFormGroup.value.SeeBusy);
  }

  async onEquipmentSelected(equipment: string) {
    await this.filterDateSlots(this.reservationCalendarFormGroup.value.Calendar
      , this.reservationCalendarFormGroup.value.Doctor
      , equipment
      , this.selectedCenters
      , this.reservationCalendarFormGroup.value.SeeBusy);
  }

  async onDoctorChange() {
    await this.filterDateSlots(this.reservationCalendarFormGroup.value.Calendar
      , null
      , this.reservationCalendarFormGroup.value.Equipment
      , this.selectedCenters
      , this.reservationCalendarFormGroup.value.SeeBusy);
  }

  async onDoctorSelected(doctor: string) {
    await this.filterDateSlots(this.reservationCalendarFormGroup.value.Calendar
      , doctor
      , this.reservationCalendarFormGroup.value.Equipment
      , this.selectedCenters
      , this.reservationCalendarFormGroup.value.SeeBusy);
  }

  async seeBusyChange() {
    await this.filterDateSlots(this.reservationCalendarFormGroup.value.Calendar
      , this.reservationCalendarFormGroup.value.Doctor
      , this.reservationCalendarFormGroup.value.Equipment
      , this.selectedCenters
      , this.reservationCalendarFormGroup.value.SeeBusy);
  }

  async onMonthChange(event: any) {

    const date = new Date();
    date.setFullYear(event.year, event.month, 1);

    // si no existe en el calendario actual
    const exists = this.reservationCalendar.ReservationSlots.findIndex(
      d => new Date(d.Date).getMonth() === date.getMonth()
        && new Date(d.Date).getFullYear() === date.getFullYear()) > -1;

    await this.filterDateSlots(date, this.reservationCalendarFormGroup.value.Doctor
        , this.reservationCalendarFormGroup.value.Equipment
        , this.selectedCenters
        , this.reservationCalendarFormGroup.value.SeeBusy);

    if (!exists) {
      this.reservationWizardService.setReservationDate(date);
      await this.reloadCalendar();
    }
  }

  async reloadCalendar() {

    try {

      this.loading = true;
      this.reservationWizardService.setSeeDraft(this.reservationCalendarFormGroup.value.SeeDraft);
      this.reservationWizardService.setSeeNoRulesRestriction(this.reservationCalendarFormGroup.value.SeeNoRulesRestriction);
      this.reservationWizardService.setSeeNoRules(this.reservationCalendarFormGroup.value.SeeNoRules);

      const calendar = await this.reservationWizardService.reservationCalendar();

      // agrego los medicos que no existen en el calendario anterior
      if (calendar.Doctors) {
        calendar.Doctors.forEach((doc) => {
          const existDoctor = this.doctors.findIndex(d => d === doc) > -1;
          if (!existDoctor) {
            this.doctors.push(doc);
          }
        });
      }

      // agrego los centros que no existen en el calendario anterior
      if (calendar.Centers) {
        calendar.Centers.forEach((cen) => {
          const existCenter = this.centers.findIndex(d => d === cen) > -1;
          if (!existCenter) {
            this.centers.push(cen);
          }
        });
      }

      // agrego los equipos que no existen en el calendario anterior
      if (calendar.Equipments) {
        calendar.Equipments.forEach((equi) => {
          const existEquipment = this.equipments.findIndex(e => e === equi) > -1;
          if (!existEquipment) {
            this.equipments.push(equi);
          }
        });
      }

      // agrego los dias libres para que el calendario se ponga verde
      if (calendar.FreeDates) {
        calendar.FreeDates.forEach((freeDate) => {
          this.reservationCalendar.FreeDates.push(freeDate);
        });
      }

      // agrego los espacios de reserva que no existen en el calendario anterior
      if (calendar.ReservationSlots) {
        calendar.ReservationSlots.forEach((reservationSlot: ReservationSlot) => {
          this.reservationCalendar.ReservationSlots.push(reservationSlot);
        });
      }
    } catch (err) {
      this.notificationToastService.error(JSON.stringify(err.error), 'Reserva');
    } finally {
      this.loading = false;
    }
  }

  save() {
  }

  reservationSlotAuthorize(reservationSlot: ReservationSlot): void {
    if (!this.authService.isOverMedicalAppointmentAth()) {
      this.confirmDialogService.confirm('Autorización', 'La reseva de sobre turno requiere autorización'
        , 'Solicitar autorización', 'Cancelar').subscribe(async (value) => {
          if (value) {
            try {
              const autho = await this.authorizationService.authorizationOverMedicalAppointmentCreate(reservationSlot);
              const dialogRef = this.dialog.open(AuthorizationDialogComponent, {
                width: '50%',
                data: autho
              });
              dialogRef.afterClosed().subscribe((result) => {
                if (result) {
                  this.setReservationSlotAuthorize(reservationSlot);
                } else {
                  this.unSelectReservationSlot(reservationSlot);
                }
              });
            } catch (err) {
              this.notificationToastService.error(JSON.stringify(err.error), 'Reserva');
            }
          } else {
            this.unSelectReservationSlot(reservationSlot);
          }
        });
    } else {
      this.setReservationSlotAuthorize(reservationSlot);
    }
  }

  private unSelectReservationSlot(reservationSlot: ReservationSlot) {
    reservationSlot.IsSelected = false;
    this.reservationWizardService.clearReservationSlot(reservationSlot);
  }

  setReservationSlotAuthorize(reservationSlot: ReservationSlot): void {
    this.confirmDialogService.confirm('Sobreturno', 'Reserva sobre turno'
      , 'Anterior al turno original', 'Posterior al turno original').subscribe(
        (value) => {
          reservationSlot.BeforeOriginalMedicalAppointment = value === true;
          reservationSlot.Authorized = true;
        });
  }

  async ngOnInit() {
    this.es = {
      firstDayOfWeek: 1,
      dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
      dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
      dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
      monthNames: ['enero', 'febrero', 'marzo', 'abril', 'mayo'
        , 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'],
      monthNamesShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
      today: 'Hoy',
      clear: 'Borrar'
    };

    this.isInit = true;

    this.reservationCalendarFormGroup.get('Calendar').valueChanges.subscribe(date => {
      this.filterDateSlots(date, this.reservationCalendarFormGroup.value.Doctor
        , this.reservationCalendarFormGroup.value.Equipment
        , this.selectedCenters
        , this.reservationCalendarFormGroup.value.SeeBusy);
    });
  }

  async createPendingReservation() {
    try {
      this.creatingPendingReservation = true;
      await this.reservationWizardService.createPendingReservation();
      this.notificationToastService.success('Se generó el turno en espera correctamente', 'Reserva');
    } catch (err) {
      this.notificationToastService.error(JSON.stringify(err.error), 'Reserva');
    } finally {
      this.creatingPendingReservation = false;
    }
  }

}
